import { getLastLogin } from '@/lib/last-login'
import { getSuperTokensAppInfo } from '@/lib/supertokens-config'
import type { AuthUser } from '@/lib/api-types'

const AUTH_DEBUG_ENABLED = process.env.NEXT_PUBLIC_AUTH_DEBUG_ENABLED === 'true'
const TRACE_KEY = 'askjyotish.auth_debug_trace'
const MAX_TRACE_ENTRIES = 40

type StorageSummary = {
  key: string
  present: boolean
  valueLength: number
}

type AuthDebugEntry = {
  at: string
  event: string
  details: Record<string, unknown>
}

function readTrace(): AuthDebugEntry[] {
  if (!AUTH_DEBUG_ENABLED || typeof window === 'undefined') {
    return []
  }

  try {
    const raw = window.sessionStorage.getItem(TRACE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw) as AuthDebugEntry[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeTrace(entries: AuthDebugEntry[]) {
  if (!AUTH_DEBUG_ENABLED || typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(TRACE_KEY, JSON.stringify(entries.slice(-MAX_TRACE_ENTRIES)))
  } catch {
    // ignore debug write failures
  }
}

function maskEmail(email: string | undefined) {
  if (!email) {
    return null
  }

  const [localPart = '', domain = ''] = email.split('@')
  const maskedLocal = localPart.length <= 2 ? `${localPart[0] ?? '*'}*` : `${localPart.slice(0, 2)}***`
  const maskedDomain = domain.length <= 2 ? `${domain[0] ?? '*'}*` : `${domain.slice(0, 2)}***`
  return `${maskedLocal}@${maskedDomain}`
}

function looksPlaceholderBirthName(
  birthName: string | null | undefined,
  email: string | null | undefined,
) {
  const normalizedName = birthName?.trim().toLowerCase() ?? ''
  const normalizedEmail = email?.trim().toLowerCase() ?? ''

  if (!normalizedName) {
    return true
  }
  if (normalizedName === 'user') {
    return true
  }
  if (normalizedName.includes('@')) {
    return true
  }
  if (normalizedEmail && normalizedName === normalizedEmail) {
    return true
  }
  return false
}

function describeStorage(
  storage: Storage | undefined,
  interestingPrefixes: string[],
): StorageSummary[] {
  if (!storage) {
    return []
  }

  const entries: StorageSummary[] = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key) {
      continue
    }
    if (!interestingPrefixes.some((prefix) => key.startsWith(prefix))) {
      continue
    }
    const value = storage.getItem(key)
    entries.push({
      key,
      present: value != null,
      valueLength: value?.length ?? 0,
    })
  }

  return entries.sort((left, right) => left.key.localeCompare(right.key))
}

function describeQuery(search: string) {
  const params = new URLSearchParams(search)
  return Array.from(params.entries()).map(([key, value]) => ({
    key,
    valueLength: value.length,
  }))
}

function collectSnapshot() {
  if (!AUTH_DEBUG_ENABLED || typeof window === 'undefined') {
    return {}
  }

  const appInfo = getSuperTokensAppInfo()
  const lastLogin = getLastLogin()

  return {
    href: window.location.href,
    origin: window.location.origin,
    pathname: window.location.pathname,
    query: describeQuery(window.location.search),
    hashLength: window.location.hash.length,
    referrer: document.referrer || null,
    supertokensAppInfo: {
      apiDomain: appInfo.apiDomain,
      websiteDomain: appInfo.websiteDomain,
      apiBasePath: appInfo.apiBasePath,
      websiteBasePath: appInfo.websiteBasePath,
    },
    lastLogin: lastLogin
      ? {
          provider: lastLogin.provider,
          email: maskEmail(lastLogin.email),
          displayNamePresent: Boolean(lastLogin.display_name),
        }
      : null,
    localStorage: describeStorage(window.localStorage, ['askjyotish.', 'supertokens', 'st-']),
    sessionStorage: describeStorage(window.sessionStorage, ['askjyotish.', 'supertokens', 'st-']),
    visibleCookies: document.cookie
      .split(';')
      .map((item) => item.trim().split('=')[0])
      .filter(Boolean)
      .sort(),
  }
}

export function clearAuthDebugTrace() {
  if (!AUTH_DEBUG_ENABLED || typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(TRACE_KEY)
  } catch {
    // ignore
  }
}

export function getAuthDebugTrace() {
  if (!AUTH_DEBUG_ENABLED) {
    return []
  }
  return readTrace()
}

export function summarizeAuthProfile(user: AuthUser | null | undefined) {
  const birthName = user?.birth_name?.trim() ?? ''
  const email = user?.email?.trim() ?? ''
  const avatarUrl = user?.avatar_url?.trim() ?? ''

  return {
    userId: user?.user_id ?? null,
    email: maskEmail(email || undefined),
    hasBirthName: Boolean(birthName),
    birthNameLength: birthName.length,
    birthNameLooksPlaceholder: looksPlaceholderBirthName(birthName, email),
    hasAvatarUrl: Boolean(avatarUrl),
    avatarUrlLength: avatarUrl.length,
  }
}

export function recordAuthDebug(event: string, details: Record<string, unknown> = {}) {
  if (!AUTH_DEBUG_ENABLED || typeof window === 'undefined') {
    return
  }

  const entry: AuthDebugEntry = {
    at: new Date().toISOString(),
    event,
    details: {
      ...collectSnapshot(),
      ...details,
    },
  }

  const nextTrace = [...readTrace(), entry]
  writeTrace(nextTrace)

  console.info('[auth-debug]', entry.event, entry)
  ;(window as Window & { __ASKJYOTISH_AUTH_DEBUG__?: AuthDebugEntry[] }).__ASKJYOTISH_AUTH_DEBUG__ =
    nextTrace
}
